import React, { useState } from "react";
import { FiCpu, FiAlertTriangle, FiCheckCircle, FiTool } from "react-icons/fi";
import { HealthRing, MiniBar, StatusBadge, healthColor, riskColor } from "./fleetHelpers";

const getRiskLabel = (v) => v >= 70 ? "High Risk" : v >= 40 ? "Moderate" : "Low Risk";

const FleetPredictivePanel = ({ d }) => {
  const [selected, setSelected] = useState(null);

  // Sort vehicles by failure risk descending
  const sorted = [...d.vehicles].sort((a, b) => b.failureRisk - a.failureRisk);
  const selectedVehicle = selected ? d.vehicles.find(v => v.id === selected) : null;

  const highRisk  = sorted.filter(v => v.failureRisk >= 70).length;
  const medRisk   = sorted.filter(v => v.failureRisk >= 40 && v.failureRisk < 70).length;
  const lowRisk   = sorted.filter(v => v.failureRisk < 40).length;

  return (
    <div className="fleet-predictive">

      {/* ── Summary Row ── */}
      <div className="fp-summary-row">
        <div className="fp-sum-card" style={{ borderColor: "#ef4444" }}>
          <FiAlertTriangle size={20} color="#ef4444" />
          <div className="fp-sum-num" style={{ color: "#ef4444" }}>{highRisk}</div>
          <div className="fp-sum-lbl">High Risk</div>
        </div>
        <div className="fp-sum-card" style={{ borderColor: "#f59e0b" }}>
          <FiTool size={20} color="#f59e0b" />
          <div className="fp-sum-num" style={{ color: "#f59e0b" }}>{medRisk}</div>
          <div className="fp-sum-lbl">Moderate Risk</div>
        </div>
        <div className="fp-sum-card" style={{ borderColor: "#10b981" }}>
          <FiCheckCircle size={20} color="#10b981" />
          <div className="fp-sum-num" style={{ color: "#10b981" }}>{lowRisk}</div>
          <div className="fp-sum-lbl">Low Risk</div>
        </div>
        <div className="fp-ai-insight">
          <FiCpu size={16} color="#667eea" />
          <span>AI predicts <strong>{highRisk} vehicles</strong> need immediate attention to prevent breakdown. Schedule service now.</span>
        </div>
      </div>

      <div className="fp-layout">

        {/* ── Vehicle Health List ── */}
        <div className="fp-vehicle-list">
          <div className="fp-list-title">Vehicle Health Ranking</div>
          {sorted.map(v => {
            const isActive = selected === v.id;
            return (
              <div
                key={v.id}
                className={`fp-veh-card ${isActive ? "fp-veh-active" : ""}`}
                onClick={() => setSelected(isActive ? null : v.id)}
              >
                <div className="fp-veh-ring">
                  <HealthRing value={v.healthScore} size={52} stroke={6} />
                  <div className="fp-veh-score" style={{ color: healthColor(v.healthScore) }}>
                    {v.healthScore}
                  </div>
                </div>

                <div className="fp-veh-info">
                  <div className="fp-veh-name">
                    {v.name}
                    <span className="fp-veh-id">{v.id}</span>
                  </div>
                  <div className="fp-veh-driver">{v.driver}</div>
                  <div className="fp-risk-row">
                    <span className="fp-risk-label">Failure Risk</span>
                    <span className="fp-risk-val" style={{ color: riskColor(v.failureRisk) }}>
                      {v.failureRisk}% · {getRiskLabel(v.failureRisk)}
                    </span>
                  </div>
                  <MiniBar value={v.failureRisk} color={riskColor(v.failureRisk)} height={5} />
                </div>

                <div className="fp-veh-right">
                  <StatusBadge status={v.status} />
                  {v.alerts.length > 0 && (
                    <span className="fp-alert-count">{v.alerts.length} alerts</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Detail Panel ── */}
        <div className="fp-detail">
          {selectedVehicle ? (
            <>
              <div className="fp-detail-title">{selectedVehicle.name} – Predictive Detail</div>

              <div className="fp-detail-health">
                <HealthRing value={selectedVehicle.healthScore} size={100} stroke={10} />
                <div className="fp-dh-center" style={{ color: healthColor(selectedVehicle.healthScore) }}>
                  {selectedVehicle.healthScore}
                  <span style={{ fontSize: 11, color: "#64748b", fontWeight: 400 }}>Health</span>
                </div>
              </div>

              <div className="fp-detail-grid">
                <div className="fp-dg-item">
                  <span className="fp-dg-label">Failure Risk</span>
                  <span className="fp-dg-val" style={{ color: riskColor(selectedVehicle.failureRisk) }}>
                    {selectedVehicle.failureRisk}%
                  </span>
                </div>
                <div className="fp-dg-item">
                  <span className="fp-dg-label">Last Service</span>
                  <span className="fp-dg-val">{selectedVehicle.lastService}</span>
                </div>
                <div className="fp-dg-item">
                  <span className="fp-dg-label">Next Service</span>
                  <span className="fp-dg-val">{selectedVehicle.nextService}</span>
                </div>
                <div className="fp-dg-item">
                  <span className="fp-dg-label">Odometer</span>
                  <span className="fp-dg-val">{selectedVehicle.odometer.toLocaleString()} km</span>
                </div>
                <div className="fp-dg-item">
                  <span className="fp-dg-label">Engine Temp</span>
                  <span className="fp-dg-val" style={{ color: selectedVehicle.engineTemp > 95 ? "#ef4444" : "#1e293b" }}>
                    {selectedVehicle.engineTemp}°C
                  </span>
                </div>
                <div className="fp-dg-item">
                  <span className="fp-dg-label">Idle Time</span>
                  <span className="fp-dg-val">{selectedVehicle.idleTime} min</span>
                </div>
              </div>

              {selectedVehicle.alerts.length > 0 && (
                <div className="fp-detail-alerts">
                  <div className="fp-da-title">Active Alerts</div>
                  {selectedVehicle.alerts.map((a, i) => (
                    <div key={i} className="fp-da-row">
                      <FiAlertTriangle size={13} color="#ef4444" />
                      {a}
                    </div>
                  ))}
                </div>
              )}

              {/* Recommended Actions */}
              <div className="fp-recommendations">
                <div className="fp-rec-title"><FiTool size={14} /> Recommended Actions</div>
                {selectedVehicle.failureRisk >= 70 && (
                  <div className="fp-rec-item fp-rec-critical">
                    🔴 Pull vehicle from service immediately. Schedule emergency maintenance.
                  </div>
                )}
                {selectedVehicle.failureRisk >= 40 && selectedVehicle.failureRisk < 70 && (
                  <div className="fp-rec-item fp-rec-warn">
                    🟡 Schedule service within 5 days to avoid breakdown risk.
                  </div>
                )}
                {selectedVehicle.failureRisk < 40 && (
                  <div className="fp-rec-item fp-rec-ok">
                    🟢 Vehicle in good health. Routine check at next service due date.
                  </div>
                )}
                {selectedVehicle.engineTemp > 95 && (
                  <div className="fp-rec-item fp-rec-critical">
                    🔴 Engine temperature above safe threshold. Check coolant levels.
                  </div>
                )}
                {selectedVehicle.idleTime > 40 && (
                  <div className="fp-rec-item fp-rec-warn">
                    🟡 Excessive idle time. Driver coaching recommended to reduce fuel waste.
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="fp-empty">
              <FiCpu size={40} color="#cbd5e1" />
              <p>Select a vehicle to view predictive analysis</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FleetPredictivePanel;
